import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";

const OFFSCREEN = { x: -1000, y: -1000 };

/**
 * Soft drifting colour blobs behind the app shell (v3 "ambient canvas").
 * Decorative and non-interactive everywhere except Smart Scout (/chat),
 * where — as in v3 — the canvas parallaxes with the cursor and a soft
 * spotlight follows it. Blob drift stays still under prefers-reduced-motion.
 */
export function AmbientBackground() {
  const { pathname } = useLocation();
  const isSmartScout = pathname === "/chat" || pathname.startsWith("/chat/");

  const [mousePos, setMousePos] = useState(OFFSCREEN);
  const [isHovered, setIsHovered] = useState(false);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });
  const targetPosRef = useRef(OFFSCREEN);
  const currentPosRef = useRef(OFFSCREEN);

  useEffect(() => {
    if (!isSmartScout) {
      setIsHovered(false);
      setCanvasOffset({ x: 0, y: 0 });
      setMousePos(OFFSCREEN);
      targetPosRef.current = OFFSCREEN;
      currentPosRef.current = OFFSCREEN;
      return;
    }

    let frame: number;

    const handleMouseMove = (e: MouseEvent) => {
      targetPosRef.current = { x: e.clientX, y: e.clientY };
      setIsHovered(true);
      // Subtle parallax so the canvas feels alive under the cursor.
      setCanvasOffset({
        x: (e.clientX - window.innerWidth / 2) * 0.025,
        y: (e.clientY - window.innerHeight / 2) * 0.025,
      });
    };

    const handleMouseLeave = () => {
      setIsHovered(false);
      setCanvasOffset({ x: 0, y: 0 });
    };

    // Lerp towards the cursor for a fluid, slightly lagging spotlight.
    const animate = () => {
      const current = currentPosRef.current;
      const target = targetPosRef.current;
      if (current.x === OFFSCREEN.x) {
        currentPosRef.current = { ...target };
      } else {
        currentPosRef.current = {
          x: current.x + (target.x - current.x) * 0.09,
          y: current.y + (target.y - current.y) * 0.09,
        };
      }
      const next = {
        x: Math.round(currentPosRef.current.x),
        y: Math.round(currentPosRef.current.y),
      };
      setMousePos((prev) => (prev.x === next.x && prev.y === next.y ? prev : next));
      frame = requestAnimationFrame(animate);
    };

    window.addEventListener("mousemove", handleMouseMove, { passive: true });
    document.addEventListener("mouseleave", handleMouseLeave);
    frame = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseleave", handleMouseLeave);
      cancelAnimationFrame(frame);
    };
  }, [isSmartScout]);

  return (
    <div
      aria-hidden="true"
      className="pointer-events-none fixed inset-0 z-0 select-none overflow-hidden"
    >
      <div
        className="absolute inset-0 opacity-90 transition-transform duration-700 ease-out [filter:blur(95px)_saturate(0.8)] motion-safe:animate-ambient-morph"
        style={{
          transform: isSmartScout
            ? `translate(${canvasOffset.x}px, ${canvasOffset.y}px)`
            : "none",
        }}
      >
        {/* Slate core */}
        <div
          className="absolute left-[20%] top-[18%] size-[800px] rounded-full"
          style={{
            background:
              "radial-gradient(circle, rgba(226, 232, 240, 0.6) 0%, rgba(203, 213, 225, 0.25) 45%, transparent 75%)",
          }}
        />
        {/* Periwinkle */}
        <div
          className="absolute -top-[10%] left-[22%] size-[680px] rounded-full will-change-transform motion-safe:animate-ambient-1"
          style={{
            background:
              "radial-gradient(circle, rgba(165, 180, 252, 0.28) 0%, rgba(199, 210, 254, 0.12) 50%, transparent 75%)",
          }}
        />
        {/* Sky */}
        <div
          className="absolute -top-[5%] right-[8%] size-[720px] rounded-full will-change-transform motion-safe:animate-ambient-2"
          style={{
            background:
              "radial-gradient(circle, rgba(147, 197, 253, 0.26) 0%, rgba(186, 230, 253, 0.1) 50%, transparent 75%)",
          }}
        />
        {/* Rose */}
        <div
          className="absolute -left-[10%] top-[25%] size-[640px] rounded-full will-change-transform motion-safe:animate-ambient-3"
          style={{
            background:
              "radial-gradient(circle, rgba(244, 143, 177, 0.24) 0%, rgba(251, 207, 232, 0.1) 45%, transparent 75%)",
          }}
        />
        {/* Apricot */}
        <div
          className="absolute right-[10%] top-[38%] size-[620px] rounded-full will-change-transform motion-safe:animate-ambient-4"
          style={{
            background:
              "radial-gradient(circle, rgba(254, 215, 170, 0.25) 0%, rgba(255, 237, 213, 0.08) 50%, transparent 75%)",
          }}
        />
        {/* Lilac */}
        <div
          className="absolute -bottom-[10%] left-[34%] size-[700px] rounded-full will-change-transform motion-safe:animate-ambient-5"
          style={{
            background:
              "radial-gradient(circle, rgba(216, 180, 254, 0.22) 0%, rgba(243, 232, 255, 0.08) 50%, transparent 75%)",
          }}
        />
      </div>

      {/* Cursor spotlight — Smart Scout only */}
      {isSmartScout && isHovered && (
        <div
          className="absolute size-[540px] -translate-x-1/2 -translate-y-1/2 rounded-full opacity-75 transition-opacity duration-500 ease-out will-change-transform [filter:blur(55px)]"
          style={{
            left: mousePos.x,
            top: mousePos.y,
            background:
              "radial-gradient(circle, rgba(255, 255, 255, 0.55) 0%, rgba(224, 231, 255, 0.28) 30%, rgba(254, 215, 170, 0.16) 55%, transparent 75%)",
          }}
        />
      )}

      {/* Grey veil for depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-surface-3/40 via-transparent to-ink-subtle/20" />
    </div>
  );
}
